import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { AuthCard } from "@/components/AuthCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <AuthCard title="Check your inbox" testid="forgot-sent"
        subtitle={<>If an account exists for <b className="text-slate-800">{email.trim()}</b>, we just emailed a link to choose a new password. It works once and expires in 60 minutes.</>}
        footer={<Link to="/login" className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline" data-testid="forgot-back-login"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>}>
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-5 text-center">
          <MailCheck className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
          <p className="text-sm text-slate-600">Didn't get it? Check spam, or <button type="button" onClick={() => setSent(false)} className="font-semibold text-primary hover:underline" data-testid="forgot-try-again">try another email</button>.</p>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Forgot your password?" subtitle="Enter the email you use for VuZuRent and we'll send you a secure link to set a new password."
      footer={<Link to="/login" className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline" data-testid="forgot-back-login"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>}>
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <div className="space-y-1.5">
          <Label htmlFor="fp-email">Email</Label>
          <Input id="fp-email" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-11" data-testid="forgot-email" />
        </div>
        <Button type="submit" disabled={loading || !email.trim()} className="w-full h-11 text-sm font-semibold gap-2" data-testid="forgot-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
        </Button>
      </form>
    </AuthCard>
  );
}
